// "Reported in the press" panel - surfaces news-search results that the
// server-side newsSearch corroborator turned up after MCCS completion.
//
// Wired into PersonPage as a top-of-page section, ABOVE the records list,
// because press coverage is high-signal context for intake (a paralegal
// reading "this person is in the news for the homicide on Aug 12" while
// looking at the record list works very differently than discovering it
// later). Strict ranking:
//
//   • Only HIGH-confidence matches are rendered by default. The LLM
//     classifier requires ≥2 corroborating signals beyond name (DOB-age,
//     city, charge type, date) for a match to count as "high".
//   • MEDIUM-confidence matches hide behind a "Show 2 weaker matches"
//     disclosure - useful but not auto-trustable.
//   • The whole panel collapses to nothing when there are no high or
//     medium hits. Most people have zero news mentions; we don't want
//     a permanently-empty card cluttering the page.
//
// Data flow: reads from the externalLatest["news"] map provided by the
// page-level useExternalLookups hook. The server's runNewsSearchForPerson
// inserts an external_lookups row with source="news" and persists the
// match list as JSON. SSE updates flow through the same hook the rest
// of the page uses, so the panel updates live as the search completes.

import { useMemo, useState } from "react";
import type { ExternalLookup } from "./ExternalSourcesPanel";

interface NewsMatch {
  chargeStatute: string;
  chargeDescription: string;
  confidence: "high" | "medium";
  reasoning: string;
  title: string;
  url: string;
  source: string;
  snippet: string;
}

interface NewsSearchPayload {
  capturedAt: string;
  query: { firstName: string; lastName: string; dob: string };
  matches: NewsMatch[];
  meta?: {
    chargesSearched: number;
    candidatesClassified: number;
    capHit: "search" | "llm" | null;
  };
}

interface NewsMentionsPanelProps {
  /** External-lookup row for source="news". Sparse - undefined when no
   *  search has been triggered yet (eg. person has no newsworthy
   *  charges, or MCCS hasn't completed). null when an attempt happened
   *  but there's no row in the latest map (defensive). */
  newsLookup: ExternalLookup | null | undefined;
}

/** Parse the persisted JSON payload safely. Returns null on shape
 *  mismatch (older rows, manual edits) so the panel can degrade silently
 *  rather than crashing the page. */
function parsePayload(raw: string | null): NewsSearchPayload | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as NewsSearchPayload;
    if (!Array.isArray(obj.matches)) return null;
    return obj;
  } catch {
    return null;
  }
}

export function NewsMentionsPanel({ newsLookup }: NewsMentionsPanelProps): JSX.Element | null {
  const [showWeak, setShowWeak] = useState(false);

  // Memoize parse + split so we don't reparse JSON on every render. Stable
  // reference matters for the conditional-render checks below.
  const { high, medium, status, capturedAt } = useMemo(() => {
    if (!newsLookup) {
      return { high: [] as NewsMatch[], medium: [] as NewsMatch[], status: "untried" as const, capturedAt: null as string | null };
    }
    const status = newsLookup.status;
    const payload = parsePayload(newsLookup.raw_json);
    const matches = payload?.matches ?? [];
    return {
      high: matches.filter((m) => m.confidence === "high"),
      medium: matches.filter((m) => m.confidence === "medium"),
      status,
      capturedAt: payload?.capturedAt ?? newsLookup.completed_at,
    };
  }, [newsLookup]);

  // No news_lookup row at all → don't render anything (most people don't
  // have a row queued).
  if (!newsLookup) return null;
  // Hide errored rows too - the LLM/CSE failures aren't actionable for
  // intake staff and surfacing them here would be confusing.
  if (status === "error" || status === "blocked") return null;
  // Completed with zero matches: render a one-line "checked, nothing
  // found" affirmation instead of hiding silently. Without this the
  // staffer can't tell whether the news search ran at all (compared to
  // it ran and was clean - meaningful signal at intake).
  if (status === "completed" && high.length === 0 && medium.length === 0) {
    return (
      <section
        className="container station"
        style={{
          marginTop: 12,
          padding: "10px 14px",
          background: "var(--color-bg-card)",
          border: "1px solid var(--color-line)",
          borderRadius: 10,
          fontFamily: "var(--font-body)",
          fontSize: 13,
          color: "var(--color-text-dim)",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--color-sage-hi)",
          }}
        />
        <span>Press search ran. No news mentions on file.</span>
      </section>
    );
  }

  // Spinner state - running. Brief, plain language; matches the rest of
  // the page's copy register.
  if (status === "queued" || status === "running") {
    return (
      <section
        className="container station"
        style={{
          marginTop: 12,
          padding: "12px 16px",
          background: "#fff8e6",
          border: "1px solid #f3deaa",
          borderRadius: 10,
          fontFamily: "var(--font-body)",
          fontSize: 13,
          color: "#5b4612",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: "#c89b1c",
            animation: "pulse-dot 1.4s ease-in-out infinite",
          }}
        />
        <span>Searching the press for mentions of this person…</span>
        <style>
          {`@keyframes pulse-dot { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }`}
        </style>
      </section>
    );
  }

  // Completed with at least one match - render the headline panel.
  return (
    <section
      className="container station"
      style={{
        marginTop: 12,
        padding: "16px 18px",
        background: "linear-gradient(180deg,#fff8ec 0%,#fff2d6 100%)",
        border: "1px solid #e6c885",
        borderRadius: 12,
        boxShadow: "0 1px 0 rgba(120,80,0,0.06)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 10,
          fontFamily: "var(--font-body)",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: "#3a2a05",
            }}
          >
            Reported in the press
          </h2>
          <span style={{ fontSize: 12, color: "#7a5b1a" }}>
            {high.length} {high.length === 1 ? "strong match" : "strong matches"}
            {medium.length > 0 ? ` · ${medium.length} weaker` : ""}
          </span>
        </div>
        {capturedAt ? (
          <span style={{ fontSize: 11, color: "#9a7d3e" }}>
            checked {formatRelative(capturedAt)}
          </span>
        ) : null}
      </header>

      <p
        style={{
          margin: "0 0 12px 0",
          fontSize: 12,
          color: "#705418",
          fontFamily: "var(--font-body)",
          fontStyle: "italic",
          lineHeight: 1.45,
        }}
      >
        Articles below match this person's name AND at least one corroborating
        detail (city, charge type, age, or date). Always confirm before relying
        on a match, even strong matches can be wrong.
      </p>

      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        {high.map((m, i) => (
          <NewsCard key={`h-${i}`} match={m} />
        ))}
        {showWeak
          ? medium.map((m, i) => <NewsCard key={`m-${i}`} match={m} />)
          : null}
      </ul>

      {medium.length > 0 ? (
        <button
          type="button"
          onClick={() => setShowWeak((v) => !v)}
          style={{
            marginTop: 12,
            padding: "6px 10px",
            background: "transparent",
            border: "1px dashed #c9a45c",
            borderRadius: 6,
            color: "#7a5b1a",
            fontFamily: "var(--font-body)",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          {showWeak
            ? `Hide ${medium.length} weaker ${medium.length === 1 ? "match" : "matches"}`
            : `Show ${medium.length} weaker ${medium.length === 1 ? "match" : "matches"}`}
        </button>
      ) : null}
    </section>
  );
}

function NewsCard({ match }: { match: NewsMatch }): JSX.Element {
  const isHigh = match.confidence === "high";
  return (
    <li
      style={{
        background: "var(--color-bg-card)",
        border: `1px solid ${isHigh ? "#b88512" : "#caa55c"}`,
        borderRadius: 8,
        padding: "10px 12px",
        fontFamily: "var(--font-body)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: 999,
            background: isHigh ? "#3a2a05" : "#7a5b1a",
            color: isHigh ? "#fbe7b6" : "#fff",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          {isHigh ? "Strong match" : "Weaker match"}
        </span>
        <span style={{ fontSize: 11, color: "#7a5b1a" }}>{match.source}</span>
      </div>
      <a
        href={match.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "block",
          fontSize: 14,
          fontWeight: 600,
          color: "#1a3a6e",
          textDecoration: "none",
          marginBottom: 4,
        }}
      >
        {match.title}
      </a>
      <p style={{ margin: "0 0 6px 0", fontSize: 12, color: "#3d3324", lineHeight: 1.5 }}>
        {match.snippet}
      </p>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          fontSize: 11,
          color: "#7a5b1a",
          alignItems: "center",
        }}
      >
        <span title={match.chargeStatute}>
          Corroborates: <strong>{shortDescription(match.chargeDescription)}</strong>
        </span>
        <span style={{ color: "#b08a4a" }}>·</span>
        <span style={{ fontStyle: "italic" }}>{match.reasoning}</span>
      </div>
    </li>
  );
}

/** "ROBBERY-INFLICTS BI OR USES FORCE OR THREAT" → "Robbery (inflicts BI…)".
 *  Keeps the corroboration line readable without losing fidelity - full
 *  description is still available via the title attr above. */
function shortDescription(desc: string): string {
  const trimmed = desc.length > 60 ? desc.slice(0, 57) + "…" : desc;
  return trimmed
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const diffSec = Math.max(0, (Date.now() - then) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
    if (diffSec < 86_400) return `${Math.round(diffSec / 3600)}h ago`;
    return `${Math.round(diffSec / 86_400)}d ago`;
  } catch {
    return "";
  }
}
